import { Ban, CheckCircle2, History, Settings } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../components/Breadcrumb';
import { ErrorState } from '../../../components/ErrorState';
import { Menu } from '../../../components/Menu';
import { PreviewTable } from '../../../components/PreviewTable';
import { Skeleton } from '../../../components/Skeleton';
import { HistoryLogsModal } from '../../../components/index-page/HistoryLogsModal';
import { ProfileBanner } from '../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../components/table/StatusText';
import { ApiError } from '../../../lib/apiError';
import { formatDateTime, toIso } from '../../../lib/formatDate';
import { CashCatalogModal } from '../components/CashCatalogModal';
import { cashCatalogsApi } from '../cashCatalogsApi';
import type { CashCatalogDescriptor, CashCatalogEntry } from '../cashCatalogTypes';

type TabKey = 'info';

/**
 * One row of one Cash Division vocabulary, on the header layout — a catalog row reads as one
 * continuous thing. It earns its keep on the count: how many requests already name a row is
 * what a reader wants before rewording or retiring it.
 */
export function CashCatalogProfilePage({ catalog }: { catalog: CashCatalogDescriptor }) {
  const { id } = useParams<{ id: string }>();

  const [entry, setEntry] = useState<CashCatalogEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      cashCatalogsApi
        .get(catalog.slug, id, signal)
        .then((next) => {
          if (!signal?.aborted) setEntry(next);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(caught instanceof ApiError ? caught : new ApiError(`Could not load this ${catalog.noun}.`, 0));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [catalog.noun, catalog.slug, id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const breadcrumb: BreadcrumbSegment[] = [
    'Cash Division',
    { label: 'Settings', to: '/cash-division/settings' },
    { label: catalog.label, to: catalog.settingsPath },
    entry ? entry.name : catalog.label,
  ];

  if (error && !entry) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (loading || !entry) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const Icon = catalog.icon;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="cash-catalog-profile"
        header={
          <ProfileHeader
            id="cash-catalog-profile-header"
            icon={<Icon className="h-6 w-6" aria-hidden="true" />}
            title={entry.name}
            meta={[`${entry.requestCount} ${entry.requestCount === 1 ? 'request' : 'requests'}`]}
            secondary={[catalog.label]}
            status={
              <StatusText tone={entry.isActive ? 'active' : 'inactive'}>
                {entry.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={entry.createdByName}
            createdAt={entry.createdAt}
            actions={
              <Menu
                id="cash-catalog-profile-settings"
                items={[
                  {
                    key: 'history',
                    label: 'History Logs',
                    icon: <History className="h-4 w-4" aria-hidden="true" />,
                    onClick: () => setHistoryOpen(true),
                    permission: 'audit-logs.read',
                  },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label={`${catalog.noun} settings`}
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          entry.isActive ? (
            <ProfileBanner
              id="cash-catalog-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message={`This ${catalog.noun} is offered on every new request to the Cash Division.`}
            />
          ) : (
            <ProfileBanner
              id="cash-catalog-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={`This ${catalog.noun} is deleted. It is no longer offered, and the requests already filed under it keep it.`}
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="cash-catalog-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'cash-catalogs.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: catalog.columnHeader, value: entry.name },
              { label: 'Vocabulary', value: catalog.label },
              { label: 'Description', value: entry.description },
              {
                label: 'Requests',
                value: <span className="font-mono tabular-nums">{entry.requestCount}</span>,
              },
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="cash-catalog-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: entry.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(entry.createdAt)} className="font-mono">
                    {formatDateTime(entry.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: entry.updatedByName },
              {
                label: 'Updated',
                value:
                  entry.updatedAt != null ? (
                    <time dateTime={toIso(entry.updatedAt)} className="font-mono">
                      {formatDateTime(entry.updatedAt)}
                    </time>
                  ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <CashCatalogModal
          catalog={catalog}
          open
          editing={entry}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="cash-catalogs"
        entityId={entry.id}
      />
    </div>
  );
}
